/**
 * Role Preview Context
 * Allows admins to preview the app as different user roles
 * 
 * SECURITY NOTE: This only affects UI display, NOT actual permissions.
 * All API calls still use the admin's real credentials and are subject
 * to Supabase RLS policies. This is a "View As" feature for demos.
 */

import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { useAuth } from './AuthContext';
import type { UserAccessLevel, Profile } from './types';

// Preview roles available to admins
export type PreviewRole = 
  | 'none'           // No preview - show real role
  | 'guest'          // View as logged out user
  | 'patient'        // Unverified patient
  | 'patient_verified'
  | 'packet_verified'
  | 'clinic'
  | 'producer';

interface RolePreviewContextType {
  // Current preview role ('none' means showing real role)
  previewRole: PreviewRole;
  // Whether preview mode is active
  isPreviewActive: boolean;
  // The effective access level (preview or real)
  effectiveAccessLevel: UserAccessLevel;
  // The effective profile (modified for preview or real)
  effectiveProfile: Profile | null;
  // Set the preview role
  setPreviewRole: (role: PreviewRole) => void;
  // Clear preview and return to real role
  clearPreview: () => void;
  // Whether the current user can use role preview
  canUseRolePreview: boolean;
}

const RolePreviewContext = createContext<RolePreviewContextType | undefined>(undefined);

// Storage key for persisting preview role
const PREVIEW_ROLE_KEY = 'admin_preview_role';

export function RolePreviewProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const [previewRole, setPreviewRoleState] = useState<PreviewRole>('none');

  // Only admins can use role preview
  const canUseRolePreview = profile?.role === 'admin';

  // Load saved preview role on mount (only for admins)
  useEffect(() => {
    if (canUseRolePreview) {
      const saved = localStorage.getItem(PREVIEW_ROLE_KEY) as PreviewRole | null;
      if (saved && saved !== 'none') {
        setPreviewRoleState(saved);
      }
    } else {
      // Clear preview if user is not admin
      setPreviewRoleState('none');
      localStorage.removeItem(PREVIEW_ROLE_KEY);
    }
  }, [canUseRolePreview]);

  const setPreviewRole = useCallback((role: PreviewRole) => {
    if (!canUseRolePreview) {
      console.warn('Role preview is only available to admin users');
      return;
    }
    
    setPreviewRoleState(role);
    
    if (role === 'none') {
      localStorage.removeItem(PREVIEW_ROLE_KEY);
    } else {
      localStorage.setItem(PREVIEW_ROLE_KEY, role);
    }
    
    console.log(`🎭 Role Preview: ${role === 'none' ? 'Disabled' : `Viewing as ${role}`}`);
  }, [canUseRolePreview]);

  const clearPreview = useCallback(() => {
    setPreviewRole('none');
  }, [setPreviewRole]);

  const isPreviewActive = previewRole !== 'none' && canUseRolePreview;

  // Calculate effective access level based on preview
  const effectiveAccessLevel: UserAccessLevel = (() => {
    if (!isPreviewActive || !profile) {
      // No preview - return real access level
      if (!profile) return 'unverified_patient';
      if (profile.role === 'admin') return 'admin';
      if (profile.role === 'clinic') return 'clinic';
      if (profile.role === 'producer') return 'producer';
      if (profile.role === 'packet_verified') return 'packet_verified';
      if (profile.role === 'patient' && profile.is_verified_patient) return 'verified_patient';
      return 'unverified_patient';
    }

    // Preview is active - return preview access level
    switch (previewRole) {
      case 'guest':
        return 'unverified_patient';
      case 'patient':
        return 'unverified_patient';
      case 'patient_verified':
        return 'verified_patient';
      case 'packet_verified':
        return 'packet_verified';
      case 'clinic':
        return 'clinic';
      case 'producer':
        return 'producer';
      default:
        return 'admin';
    }
  })();

  // Create effective profile for preview
  const effectiveProfile: Profile | null = (() => {
    if (!profile) return null;
    
    if (!isPreviewActive) {
      return profile; // Return real profile
    }

    // Create a modified profile for preview
    // Note: This is for UI display only - API calls use real credentials
    const previewProfiles: Record<PreviewRole, Partial<Profile>> = {
      none: {},
      guest: {
        role: 'patient',
        is_verified_patient: false,
        display_name: 'Guest User (Preview)',
      },
      patient: {
        role: 'patient',
        is_verified_patient: false,
        display_name: 'Patient (Preview)',
      },
      patient_verified: {
        role: 'patient',
        is_verified_patient: true,
        display_name: 'Verified Patient (Preview)',
      },
      packet_verified: {
        role: 'packet_verified',
        is_verified_patient: false,
        display_name: 'Packet Verified (Preview)',
      },
      clinic: {
        role: 'clinic',
        is_verified_patient: false,
        display_name: 'Clinic Staff (Preview)',
        clinic_id: profile.clinic_id || '00000000-0000-0000-0000-000000000001',
      },
      producer: {
        role: 'producer',
        is_verified_patient: false,
        display_name: 'Producer (Preview)',
      },
    };

    return {
      ...profile,
      ...previewProfiles[previewRole],
    } as Profile;
  })();

  return (
    <RolePreviewContext.Provider
      value={{
        previewRole,
        isPreviewActive,
        effectiveAccessLevel,
        effectiveProfile,
        setPreviewRole,
        clearPreview,
        canUseRolePreview,
      }}
    >
      {children}
    </RolePreviewContext.Provider>
  );
}

export function useRolePreview() {
  const context = useContext(RolePreviewContext);
  if (!context) {
    throw new Error('useRolePreview must be used within RolePreviewProvider');
  }
  return context;
}

